// Tag analytics: derives lure-type metrics from sim-types.json + the mock
// SEND_LOG. sim-types.json is the canonical tag taxonomy; the dataset in
// data.js uses a legacy lowercase tag vocabulary that we map onto it.
//
// Postgres mapping:
//   - `simulation_tags` (sim_id, tag_key) keyed against a `tag_taxonomy`
//     reference table (key, label, description).
//   - `mv_tag_click_rates(scope_hash, tag_key, sends, clicks, click_rate)`
//     refreshed nightly to avoid client-side joins over the send log.
//
// Integration hook: `tagApi.useTagBreakdownQuery({ scope, period })` returns
// the same shape as `clickRateByTag` and replaces the in-memory aggregator.
import simTypesData from './sim-types.json';
import { SIMS, SEND_LOG, SIM_CATALOG, DATA_EPOCH } from './data';

// Canonical sim-tag taxonomy. Consolidates the published psychological
// triggers (urgency, authority, brand, fear, reward, curiosity, social
// proof) with the pretext themes (credential prompt, security alert,
// file share, payment, delivery, HR / recruitment, collaboration) into
// a tight 14-tag set. Each sim picks one or more — usually a trigger
// plus a theme — so charts can read either dimension.
export const TAG_LABELS = {
    // Psychological triggers
    urgency:            'Urgency',
    authority:          'Authority',
    brandImpersonation: 'Brand impersonation',
    fearThreat:         'Fear / threat',
    rewardGreed:        'Reward / greed',
    curiosity:          'Curiosity',
    socialProof:        'Social proof',
    // Pretext themes
    credentialPrompt:   'Credential prompt',
    securityAlert:      'Security alert',
    fileShare:          'File / storage share',
    payment:            'Payment / finance',
    delivery:           'Delivery / parcel',
    hrRecruitment:      'HR / recruitment',
    collaboration:      'Meeting / collaboration',
};

export const TAG_DESCRIPTIONS = {
    urgency:            'Time pressure or deadline framing - "act now", "expires today".',
    authority:          'Implied authority - IT, manager, exec, HR, regulator - or compliance obligation framing.',
    brandImpersonation: 'Spoofed brand (Microsoft, Google, Apple, Amazon, DocuSign) used as the sender or trust signal.',
    fearThreat:         'Alarming or threatening message - account locked, suspicious activity, legal consequence.',
    rewardGreed:        'Promised payout - voucher, prize, refund, bonus, gift card or other monetary upside.',
    curiosity:          'Open-loop hook - "see what your colleague said", topical/seasonal intrigue, mysterious attachment.',
    socialProof:        'Familiar contact, helpfulness/reciprocity, or "your colleague already did this" framing.',
    credentialPrompt:   'Password reset, MFA challenge or login flow as the call to action.',
    securityAlert:      'Account security warning, malware quarantine, IT system maintenance, suspension notice.',
    fileShare:          'Shared document, mailbox storage / quota alert, or cloud-drive notification.',
    payment:            'Invoice, payroll change, tax / government finance or other payment-related pretext.',
    delivery:           'Parcel / courier notification, customs notice or consumer-shopping confirmation.',
    hrRecruitment:      'HR benefits, leave, training assignment or external job-recruitment pitch.',
    collaboration:      'Meeting invite, calendar reschedule, voicemail / chat mention or Teams-style notification.',
};

// Legacy raw tags carried on a few SIMS entries in data.js. Keeps older
// fixtures interoperable with the new taxonomy by folding the imprecise
// strings into the closest canonical key. Anything not listed here is
// dropped from the lure analytics.
const LEGACY_MAP = {
    'monetary request':   'payment',
    'implied authority':  'authority',
    urgency:              'urgency',
    'social engineering': 'socialProof',
    'spelling error':     'curiosity',
    // Simulation-service catalogue vocabulary (sim-types.json tagTaxonomy)
    // folded into the canonical 14-key set.
    impliedAuthority:     'authority',
    monetaryIncentive:    'payment',
    reward:               'rewardGreed',
    personalization:      'socialProof',
    accountAction:        'securityAlert',
    credentialHarvesting: 'credentialPrompt',
    exclusivity:          'rewardGreed',
    marketing:            'rewardGreed',
    callToAction:         'urgency',
};

export function canonicalTags(rawTags = []) {
    const out = new Set();
    for (const t of rawTags || []) {
        if (TAG_LABELS[t]) out.add(t);
        else if (LEGACY_MAP[t]) out.add(LEGACY_MAP[t]);
    }
    return [...out];
}

// sim name -> canonical tag list. Base mapping from each hydrated SIMS
// entry's tags (already sourced from the simulation-service catalogue),
// overridden by the live catalogue itself and the bundled sim-types.json
// export. Built lazily and rebuilt whenever ./data re-hydrates (DATA_EPOCH).
let TAGS_BY_SIM = new Map();
let tagsEpoch = '';

// Server-derived lure tags (reportsb_agg.simulation_tags via the 'sim_tags' aggregation).
// Injected by the shell on load; wins over the catalogue tags (empty in production) and
// may add sims the client SIMS list doesn't carry.
let SERVER_TAGS = new Map();
let serverTagsEpoch = 0;
export function setServerSimTags(rows) {
    SERVER_TAGS = new Map();
    for (const r of rows || []) {
        const tags = canonicalTags(r?.tags || []);
        if (r?.simName && tags.length) SERVER_TAGS.set(String(r.simName), tags);
    }
    serverTagsEpoch++;
}

function tagIndex() {
    const epochKey = `${DATA_EPOCH}:${serverTagsEpoch}`;
    if (tagsEpoch === epochKey) return TAGS_BY_SIM;
    TAGS_BY_SIM = new Map();
    for (const sim of SIMS) TAGS_BY_SIM.set(sim.name, canonicalTags(sim.tags || []));
    const applyOverrides = (entries) => {
        for (const entry of entries || []) {
            const key = entry.simulationName || entry.id;
            if (!key) continue;
            const tags = canonicalTags(entry.tags || []);
            if (tags.length && TAGS_BY_SIM.has(String(key))) TAGS_BY_SIM.set(String(key), tags);
        }
    };
    applyOverrides(simTypesData.simulations);
    applyOverrides(SIM_CATALOG);
    for (const [name, tags] of SERVER_TAGS) TAGS_BY_SIM.set(name, tags);
    tagsEpoch = epochKey;
    return TAGS_BY_SIM;
}

export function tagsForSim(simName) {
    return tagIndex().get(simName) || [];
}

export function tagLabel(key) {
    return TAG_LABELS[key] || key;
}

const isClick = (status) => status === 'clicked' || status === 'clicked_2nd';

// Aggregate click-rate per canonical tag across a SEND_LOG-shaped event list.
// Each event contributes once per tag, so a sim with 3 tags counts in 3
// buckets (which is what we want for "what lure types work?").
export function clickRateByTag(events) {
    const stats = {};
    for (const e of events) {
        const tags = tagsForSim(e.simName);
        for (const t of tags) {
            if (!stats[t]) stats[t] = { tag: t, label: tagLabel(t), sends: 0, clicks: 0, reports: 0 };
            stats[t].sends++;
            if (isClick(e.status)) stats[t].clicks++;
            if (e.status === 'reported') stats[t].reports++;
        }
    }
    return Object.values(stats).map((s) => ({
        ...s,
        rate: s.sends ? (s.clicks / s.sends) * 100 : 0,
        reportRate: s.sends ? (s.reports / s.sends) * 100 : 0,
    }));
}

// All tags that ever appear in the dataset, sorted by overall send volume.
// Used to fix column order in the dept heatmap so rows don't reshuffle when
// the visible tag set changes.
export function allTagKeys() {
    return clickRateByTag(SEND_LOG)
        .sort((a, b) => b.sends - a.sends)
        .map((s) => s.tag);
}

// User-level susceptibility: clicks per tag for one email. Returns rows with
// zero sends omitted so the radar chart only shows lures the user has
// actually been exposed to.
export function tagsForUser(email) {
    if (!email) return [];
    const events = SEND_LOG.filter(
        (e) => e.email === email || `${e.firstName} ${e.lastName}` === email,
    );
    return clickRateByTag(events);
}

// Department roll-up: { tag, dept, sends, clicks, rate } rows for the heatmap.
export function tagsByDept() {
    const cells = {};
    for (const e of SEND_LOG) {
        const tags = tagsForSim(e.simName);
        for (const t of tags) {
            const key = `${e.dept}::${t}`;
            if (!cells[key]) cells[key] = { dept: e.dept, tag: t, sends: 0, clicks: 0 };
            cells[key].sends++;
            if (isClick(e.status)) cells[key].clicks++;
        }
    }
    return Object.values(cells).map((c) => ({
        ...c,
        rate: c.sends ? (c.clicks / c.sends) * 100 : 0,
    }));
}

// Coverage: share of sims in the catalogue that carry at least one tag.
// Used to render a "tags cover X% of sims" hint on charts so a sparse
// dataset can't masquerade as a confident signal.
export function tagCoverage() {
    let tagged = 0;
    for (const sim of SIMS) {
        if (tagsForSim(sim.name).length > 0) tagged++;
    }
    return {
        tagged,
        total: SIMS.length,
        pct: SIMS.length ? Math.round((tagged / SIMS.length) * 100) : 0,
    };
}
